/**
 * FixedStepDriver — frame-time → fixed-tick accumulator.
 *
 * Lives in shared (not the client) so frame-partition independence is unit-testable:
 * feeding the same wall-clock timeline as 120×16.7ms frames or 20×100ms frames must
 * produce bitwise-identical tick states (rule R41).
 *
 * Overload stance: excess accumulated time beyond MAX_TICKS_PER_FRAME is DROPPED —
 * sustained overload plays in slow motion and a backgrounded tab resumes cleanly;
 * there is never a catch-up burst or a teleport.
 */
import { MAX_TICKS_PER_FRAME, PHYSICS_FIXED_DT } from '../protocol/physics.js';
/**
 * Carries an edge-triggered input (jump) from the frame that observed it to the tick that
 * consumes it.
 *
 * Physics runs on a fixed tick while input is sampled per render frame, and above 60 fps
 * most frames run NO tick — at 144 Hz only ~42% of them do. Without a latch, a press
 * sampled on a tick-less frame is overwritten by the next frame's sample, and even when it
 * survives, its delay varies by a whole tick. Both read to the player as "the game missed
 * my input".
 *
 * Lives in shared so the delivery guarantee is covered by the engine suite (R37).
 */
export class InputLatch {
    pending = false;
    /** Record an edge. Idempotent until consumed. */
    press() {
        this.pending = true;
    }
    /** Is an edge waiting? Does not clear it. */
    get armed() {
        return this.pending;
    }
    /**
     * Clear the edge because a tick consumed it. `consumed` is false for a tick that did not
     * actually simulate (a frozen tick waiting on chunk data), which must not eat the input.
     */
    settle(consumed) {
        if (consumed)
            this.pending = false;
    }
    reset() {
        this.pending = false;
    }
}
/** Frame delta clamp in ms (mirrors the render loop's own clamp). */
const MAX_FRAME_MS = 100;
export class FixedStepDriver {
    accumulator = 0;
    /**
     * Advance by a frame's wall-clock delta, invoking `tick` once per whole fixed step
     * (0..MAX_TICKS_PER_FRAME times). Returns the number of ticks run.
     */
    feed(frameMs, tick) {
        if (!(frameMs > 0))
            return 0; // guards NaN and negative deltas
        this.accumulator += Math.min(frameMs, MAX_FRAME_MS) / 1000;
        let ticks = 0;
        while (this.accumulator >= PHYSICS_FIXED_DT && ticks < MAX_TICKS_PER_FRAME) {
            tick();
            this.accumulator -= PHYSICS_FIXED_DT;
            ticks++;
        }
        // Drop whatever we couldn't afford; keep the sub-tick remainder for interpolation
        if (this.accumulator >= PHYSICS_FIXED_DT) {
            this.accumulator = this.accumulator % PHYSICS_FIXED_DT;
        }
        return ticks;
    }
    /** Interpolation factor between the previous and current tick states, 0..1. */
    get alpha() {
        return this.accumulator / PHYSICS_FIXED_DT;
    }
    /** Discard any accumulated time (teleports, pauses, world switches). */
    reset() {
        this.accumulator = 0;
    }
}
//# sourceMappingURL=driver.js.map